import React, { createContext, useContext, useMemo } from 'react';
import { FocusSource } from '@/hooks/use-component-focus';
import { gql } from '@apollo/client';
import { ItemListOption } from './item-context-types';
import {
  ItemRecommendation_InfoFieldsFragment,
  ItemRecommendations_InfoListQuery,
  ItemRecommendations_InfoListQueryVariables,
} from '@/types';
import { ListOption } from '@/components/list-item';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useNewOrderContext } from './new-order-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import { VARIANTS_PACK_FRAGMENT } from './items-context-queries';

export type ItemRecommendationListOption = ListOption<{
  item: ItemListOption;
  itemRecommendation: ItemRecommendation_InfoFieldsFragment;
}>;

const ITEM_RECOMMENDATION_INFO_FRAGMENT = gql`
  fragment ItemRecommendation_InfoFields on GetItemRecommendationResponse {
    item_recommendation_id
    rank
    variant_pack {
      ...ItemsContext_VariantsPackFields
    }
  }
  ${VARIANTS_PACK_FRAGMENT}
`;

export const GET_ITEM_RECOMMENDATIONS_QUERY = gql`
  query ItemRecommendations_InfoList(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    getItemRecommendations(
      supplier_id: $supplierUUID
      restaurant_id: $restaurantUUID
    ) {
      ...ItemRecommendation_InfoFields
    }
  }
  ${ITEM_RECOMMENDATION_INFO_FRAGMENT}
`;

export interface ItemRecommendationMapType {
  [variantPackUUID: string]: ItemRecommendation_InfoFieldsFragment;
}

export type FocusResetFunc = (focusSource: FocusSource) => void;

export interface ItemRecommendationsContextType {
  itemRecommendationMap: ItemRecommendationMapType;
  itemRecommendations: ItemRecommendationListOption[];
  loading?: boolean;
}

export const ItemRecommendationsContext =
  createContext<ItemRecommendationsContextType | null>(null);

export const useItemRecommendationsContext = () => {
  const context = useContext(ItemRecommendationsContext);
  if (!context) {
    throw new Error(
      'useItemRecommendationsContext requires an itemRecommendationsContextProvider',
    );
  }

  return context;
};

interface Props {
  children?: React.ReactNode;
}

export const ItemRecommendationsContextProvider: React.FC<Props> = ({
  children,
}) => {
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const itemQueryVariables = useItemQueryVariables();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();

  const itemRecommendationsEnabled = useDynamicConfigValue(
    'item_recommendations_enabled',
  );

  const { data, loading } = useWrappedQuery<
    ItemRecommendations_InfoListQuery,
    ItemRecommendations_InfoListQueryVariables
  >(GET_ITEM_RECOMMENDATIONS_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'no-cache',
    skip: !itemRecommendationsEnabled || !restaurantUUID || !supplierUUID,
    variables: {
      ...itemQueryVariables,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  const value = useMemo(() => {
    const itemRecommendationList = data?.getItemRecommendations || [];
    const hasRecommendations = itemRecommendationList.length;
    if (!itemRecommendationsEnabled || !hasRecommendations) {
      return {
        itemRecommendationMap: {},
        itemRecommendations: [],
      };
    }
    const itemRecommendationMap =
      itemRecommendationList.reduce<ItemRecommendationMapType>(
        (acc, itemRecommendation) => {
          if (itemRecommendation.variant_pack) {
            acc[itemRecommendation.variant_pack.uuid] = itemRecommendation;
          }

          return acc;
        },
        {},
      );

    const itemRecommendations = itemRecommendationList.reduce<
      ItemRecommendationListOption[]
    >((acc, itemRecommendation) => {
      const variantPack = itemRecommendation.variant_pack;
      if (variantPack) {
        const item = adaptAndGroupVariantPacks([variantPack])[0];
        if (item) {
          if (item.metadata) {
            item.metadata.itemRecommendationUUID =
              itemRecommendation.item_recommendation_id;
          }

          acc.push({
            metadata: {
              item,
              itemRecommendation,
            },
            title: variantPack.item.display_name,
            value: variantPack.uuid,
          });
        }
      }

      return acc;
    }, []);

    return {
      itemRecommendationMap,
      itemRecommendations,
      loading,
    };
  }, [
    adaptAndGroupVariantPacks,
    data?.getItemRecommendations,
    itemRecommendationsEnabled,
    loading,
  ]);
  return (
    <ItemRecommendationsContext.Provider value={value}>
      {children}
    </ItemRecommendationsContext.Provider>
  );
};

export const DummyItemRecommendationsContextProvider: React.FC<Props> = ({
  children,
}) => {
  const value = {
    itemRecommendationMap: {},
    itemRecommendations: [],
  };
  return (
    <ItemRecommendationsContext.Provider value={value}>
      {children}
    </ItemRecommendationsContext.Provider>
  );
};
